import { Helmet } from "react-helmet-async";
import "./App.css";
import Home from "./apps/Home";
import Login from "./views/Login/Login";
import { LoginContext } from "./contexts/LoginContext";
import { useCallback, useEffect, useRef, useState } from "react";

import { Routes, Route } from "react-router-dom";
import OrderDetail from "./components/sections/OrderDetail/OrderDetail";
import Header from "./components/sections/Header";
import { useDispatch, useSelector, useStore } from "react-redux";
import LoadingFailedPage from "./components/sections/LoadingPages/RejectedPage";
import CartDetail from "./components/sections/CartDetail/CartDetail";
import {
  getCart,
  saveCartItem,
  setDataLoad,
} from "./store/slices/CartSlice/CartSlice";
import CartNotice from "./components/atoms/CartNotice/CartNotice";
import {
  getAdminVars,
  getUserById,
  fetchCurrentVersion,
  checkNewVersionAvailable,
} from "./store/slices/AdminSlice/AdminSlice";
import GettingReadyPage from "./components/sections/LoadingPages/LoadingPage";
import Settings from "./components/sections/AdminPages/Settings";
import CheckoutProcess from "./components/sections/OrderSubmission/CheckoutProcess";
import TrackMatchPopup from "./components/atoms/TrackMatchPopup/TrackMatchPopup";
import LoaderSpinner from "./components/sections/LoadingPages/LoaderSpinner";
import CustomerHistory from "./components/sections/HistoryViewer/CustomerHistory";
import CreditDetail from "./components/sections/OrderDetail/CreditDetail";
import QuoteDetail from "./components/sections/OrderDetail/QuoteDetail";
import CreateUser from "./components/sections/AdminPages/CreateUser";
import EditUser from "./components/sections/AdminPages/EditUser";
import { getCustomerWarehouseCutoff } from "./store/slices/CustomerSlice/CustomerSlice";
import VersionUpdateBanner from "./components/atoms/VersionUpdateBanner/VersionUpdateBanner";
import AccountSetupPage from "./components/sections/LoadingPages/AccountSetupPage";

function App() {
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [groups, setGroups] = useState([]);

  const dispatch = useDispatch();

  const { rejected, fatal } = useSelector((state) => state.loadingTracker);
  const { cartItems, dataLoad } = useSelector((state) => state.cart);
  const {
    primaryColor,
    secondaryColor,
    adminLoading,
    title,
    titleImage,
    backgroundImage,
    currentVersion,
  } = useSelector((state) => state.admin);

  const { showLoader: customerLoader } = useSelector((state) => state.customer);
  const { showLoader: orderLoader } = useSelector((state) => state.orders);

  const previousCartRef = useRef([]);
  const debounceMapRef = useRef({});
  // product_code -> true while a save for that product is waiting on the server
  const inFlightRef = useRef({});
  // product_code -> the latest change made while that save was out
  const queuedRef = useRef({});
  const store = useStore();

  // Only one save per product at a time. A second save sent before the first
  // returns would still carry cart_item_key 0, and the backend would treat it
  // as the product being added again from another tab.
  const sendCartItem = useCallback(
    (item) => {
      const send = (toSave) => {
        const { product_code } = toSave;
        // Use the saved line's key if the store has one by now -- the item
        // captured when the change was made may still have key 0.
        const current = store
          .getState()
          .cart.cartItems.find((i) => i.product_code === product_code);
        const cartItem = {
          ...toSave,
          cart_item_key: current?.cart_item_key || toSave.cart_item_key,
        };

        inFlightRef.current[product_code] = true;
        dispatch(saveCartItem({ cartItem })).finally(() => {
          delete inFlightRef.current[product_code];
          const queued = queuedRef.current[product_code];
          if (queued) {
            delete queuedRef.current[product_code];
            send(queued);
          }
        });
      };
      send(item);
    },
    [dispatch, store],
  );

  // This is maybe a weird spot to have this, but the cart could potentially be edited from 2 or more spots.
  //  So, to not duplicate logic, it needs to be at the most common parent level.
  const onItemChange = useCallback(
    (item) => {
      if (dataLoad) {
        console.log("Data still loading, setting dataLoad to false");
        dispatch(setDataLoad(false));
        return;
      }

      const { product_code } = item;

      // Clear any existing timer for this product
      if (debounceMapRef.current[product_code]) {
        clearTimeout(debounceMapRef.current[product_code]);
      }

      // Set new timer
      debounceMapRef.current[product_code] = setTimeout(() => {
        if (inFlightRef.current[product_code]) {
          queuedRef.current[product_code] = item;
        } else {
          sendCartItem(item);
        }
        delete debounceMapRef.current[product_code]; // cleanup
      }, 500);
    },
    [dataLoad, dispatch, sendCartItem],
  );

  const isEqual = (prevItem, item) => {
    return prevItem.quantity === item.quantity;
  };

  useEffect(() => {
    dispatch(getCart());
    dispatch(getUserById(-1));
    dispatch(getCustomerWarehouseCutoff());
    dispatch(fetchCurrentVersion("CSFA"));
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated || fatal) return;
    const prevCart = previousCartRef.current;

    cartItems.forEach((item) => {
      const prevItem = prevCart.find(
        (p) => p.product_code === item.product_code,
      );
      if (!prevItem || !isEqual(prevItem, item)) {
        if (item.quantity === "" || item.quantity === 0) return;
        onItemChange(item); // pass just the new item. Backend can figure it out.
      }
    });

    // If dataLoad is true (cart just loaded) and we have no items to process,
    // we still need to set dataLoad to false so future item additions work
    if (dataLoad && cartItems.length === 0) {
      dispatch(setDataLoad(false));
    }

    previousCartRef.current = cartItems;
  }, [cartItems, onItemChange, isAuthenticated, fatal, dataLoad, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!primaryColor) dispatch(getAdminVars());
  }, [isAuthenticated, dispatch, primaryColor]);

  // Poll for new version every 5 minutes
  useEffect(() => {
    if (!isAuthenticated || !currentVersion || fatal) return;

    const FIVE_MINUTES = 5 * 60 * 1000;
    const intervalId = setInterval(() => {
      dispatch(
        checkNewVersionAvailable({
          name: "CSFA",
          currentVersion,
        }),
      );
    }, FIVE_MINUTES);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, currentVersion, fatal, dispatch]);

  // Update document title and favicon when Redux values change
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update favicon if titleImage is available
    if (titleImage?.image_url) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = titleImage.image_url;
    }
  }, [title, titleImage]);

  return (
    <>
      <LoginContext.Provider
        value={{
          isAuthenticated,
          setAuthenticated,
          username,
          setUsername,
          groups,
          setGroups,
        }}
      >
        <Login groups={groups} />
        <Helmet>
          <style>
            {`
        :root {
          --primary-color: ${primaryColor};
          --primary-color-dark: #1a85c6;
          --primary-color-light: #6fc9f2;
          --secondary-color: ${secondaryColor};
          --accent-color: #f5c518;
          --background-color: #f8f9fa;
          --surface-color: #ffffff;
          --text-primary-color: #212529;
          --text-secondary-color: #6c757d;
          --border-color: #dee2e6;
        }

        body {
          ${
            backgroundImage?.image_url
              ? `
            background-image: url("${backgroundImage.image_url}");
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            background-attachment: fixed;
          `
              : ""
          }
        }

      `}
          </style>
        </Helmet>
        {/*
          A fatal error (no customer number on the account) is the end of the
          road: render the explanation and nothing else. Nothing below this
          point may mount, because every screen fetches on mount and every
          CSFA endpoint will answer with the same 412.

          The loading gate deliberately keys on adminLoading only. It must
          never key on a flag a screen inside the tree owns (productLoading
          used to be here): pending would unmount that screen, its rejection
          would remount it, and the mount effect would fire the failing
          request again forever.
        */}
        {fatal ? (
          <AccountSetupPage />
        ) : adminLoading ? (
          <GettingReadyPage />
        ) : (
          <>
            <Header />
            {isAuthenticated && !rejected && (
              <Routes>
                <Route path={"/"} element={<Home />} />
                <Route path={"/view-order/:id"} element={<OrderDetail />} />
                <Route path={"/view-cart"} element={<CartDetail />} />
                <Route path={"/*"} element={<LoadingFailedPage />} />
                <Route path={"/settings"} element={<Settings />} />
                <Route path={"/settings/site-admin"} element={<Settings />} />
                <Route path={"/settings/user-admin"} element={<Settings />} />
                <Route
                  path={"/settings/create-user"}
                  element={<CreateUser />}
                />
                <Route path={"settings/edit-user/:id"} element={<EditUser />} />

                <Route path={"/checkout"} element={<CheckoutProcess />} />
                <Route path={"/view-history"} element={<CustomerHistory />} />
                <Route path={"/credit-detail/:id"} element={<CreditDetail />} />
                <Route path={"/quote-detail/:id"} element={<QuoteDetail />} />
              </Routes>
            )}
            {isAuthenticated && rejected && <LoadingFailedPage />}
            <TrackMatchPopup />
          </>
        )}
      </LoginContext.Provider>
      <LoaderSpinner show={!fatal && (customerLoader || orderLoader)} />
      {!fatal && <VersionUpdateBanner />}
      {!fatal && <CartNotice />}

      {/* Version Tag */}
      {isAuthenticated && !fatal && (
        <div className="fixed bottom-2 left-2 z-50">
          <div className="bg-gray-800 bg-opacity-80 text-white text-xs px-2 py-1 rounded-full shadow-lg backdrop-blur-sm">
            <span className="font-mono">{currentVersion}</span>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
